import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import {
  getCrossProjectCriticalPaths,
  WbsItemCriticalPathResponse,
} from '../../../../infrastructure/api/wbs-item-critical-paths'
import { CriticalPathLinkData } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/GanttCriticalPath/CriticalPathContext'
import { WbsItemInfo } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/types'
import { WbsItemLinkSummary } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/WbsItemLink/WbsItemLinkDialog'
import { CalendarDateVO } from '../../../../domain/value-object/CalendarDateVO'
import { dateV2Service } from '../../../../domain/value-object/DateV2'
import { dateTimeV2Service } from '../../../../domain/value-object/DateTimeV2'
import {
  ProgramManagementRow,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from '../programManagement'

export type UseCrossProjectCriticalPathDataReturn = {
  criticalPaths: CriticalPathLinkData[]
  wbsItemInfoMap: ReadonlyMap<string, WbsItemInfo>
  dialogItems: WbsItemLinkSummary[]
}

/**
 * プログラム管理画面用のクロスプロジェクトCP取得hook。
 * PROJECT行のuuidをprojectUuidsとして使用し、
 * APIレスポンスをCriticalPathLinkData[]に変換する。
 */
export const useCrossProjectCriticalPathData = (
  rows: ProgramManagementRow[],
  gridApi: GridApi<ProgramManagementRow> | null
): UseCrossProjectCriticalPathDataReturn => {
  const [criticalPaths, setCriticalPaths] = useState<CriticalPathLinkData[]>([])
  const [wbsItemInfoMap, setWbsItemInfoMap] = useState<
    ReadonlyMap<string, WbsItemInfo>
  >(new Map())
  const fetchedForRef = useRef<string | null>(null)

  const fetchCriticalPaths = useCallback(async () => {
    if (!gridApi || rows.length === 0) return

    // PROJECT行のprojectUuidを抽出
    const projectUuids = rows
      .filter(
        r => r.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT && r.projectUuid
      )
      .map(r => r.projectUuid!)

    if (projectUuids.length === 0) {
      setCriticalPaths([])
      setWbsItemInfoMap(new Map())
      return
    }

    // 同じプロジェクト群に対して二重フェッチを防止
    const key = projectUuids.sort().join(',')
    if (fetchedForRef.current === key) return
    fetchedForRef.current = key

    try {
      const response = await getCrossProjectCriticalPaths(projectUuids)
      if (response.hasError) return

      const serverData = (response.json as WbsItemCriticalPathResponse[]) ?? []

      // AG Grid の表示行から projectPlanUuid → rowIndex マッピングを構築
      const projectPlanUuidToRowIndex = new Map<string, number>()
      gridApi.forEachNodeAfterFilterAndSort(node => {
        if (node.data?.uuid && node.rowIndex != null && node.rowIndex >= 0) {
          projectPlanUuidToRowIndex.set(node.data.uuid, node.rowIndex)
        }
      })

      // wbsItemInfoMap 構築（非表示行の displayName/icon 解決用）
      // CriticalPathLinkData.fromWbsItemUuid は wbsItemUuid を保持するため、
      // infoMap のキーも wbsItemUuid を使用する
      // 端点（行uuid = projectPlanUuid）から祖先 PROJECT 行を辿り、プロジェクト名/アイコンを解決する。
      // プロジェクト側CPの端点もポップアップ等でプロジェクトを表示できるようにする。
      const rowByUuid = new Map(rows.map(r => [r.uuid, r]))
      const resolveProject = (
        projectPlanUuid: string
      ): { name: string; iconUrl?: string } | undefined => {
        const row = rowByUuid.get(projectPlanUuid)
        if (!row) return undefined
        for (let i = row.treeValue.length - 1; i >= 0; i--) {
          const ancestor = rowByUuid.get(row.treeValue[i])
          if (
            ancestor?.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT &&
            ancestor.projectUuid
          ) {
            return {
              name: ancestor.displayName ?? '',
              iconUrl: ancestor.iconUrl,
            }
          }
        }
        return undefined
      }

      const infoMap = new Map<string, WbsItemInfo>()
      for (const s of serverData) {
        if (s.fromWbsItem) {
          const fromProject = resolveProject(s.fromProjectPlanUuid)
          infoMap.set(s.fromWbsItemUuid, {
            displayName: s.fromWbsItem.displayName ?? '',
            iconUrl: s.fromWbsItem.typeDto?.iconUrl,
            projectName: fromProject?.name,
            projectIconUrl: fromProject?.iconUrl,
          })
        }
        if (s.toWbsItem) {
          const toProject = resolveProject(s.toProjectPlanUuid)
          infoMap.set(s.toWbsItemUuid, {
            displayName: s.toWbsItem.displayName ?? '',
            iconUrl: s.toWbsItem.typeDto?.iconUrl,
            projectName: toProject?.name,
            projectIconUrl: toProject?.iconUrl,
          })
        }
      }

      // CriticalPathLinkData への変換
      // プログラム管理画面では行のuuid = projectPlanUuid であるため、
      // fromProjectPlanUuid/toProjectPlanUuid で rowIndex を解決する
      const links: CriticalPathLinkData[] = serverData.map(s => {
        const createdAtVo = dateTimeV2Service.fromApiResponseToVo(s.createdAt)
        const updatedAtVo = dateTimeV2Service.fromApiResponseToVo(s.updatedAt)
        return {
          uuid: s.uuid,
          fromRowIndex:
            projectPlanUuidToRowIndex.get(s.fromProjectPlanUuid) ?? -1,
          toRowIndex: projectPlanUuidToRowIndex.get(s.toProjectPlanUuid) ?? -1,
          fromDate: CalendarDateVO.of(s.fromDate),
          toDate: CalendarDateVO.of(s.toDate),
          fromWbsItemUuid: s.fromWbsItemUuid,
          toWbsItemUuid: s.toWbsItemUuid,
          fromProjectPlanUuid: s.fromProjectPlanUuid,
          toProjectPlanUuid: s.toProjectPlanUuid,
          editable: false,
          ...(s.color != null && { color: s.color }),
          ...(s.description != null && { description: s.description }),
          createdAt: createdAtVo
            ? dateTimeV2Service.format(createdAtVo)
            : undefined,
          createdBy: s.createdBy,
          updatedAt: updatedAtVo
            ? dateTimeV2Service.format(updatedAtVo)
            : undefined,
          updatedBy: s.updatedBy,
        }
      })

      setCriticalPaths(links)
      setWbsItemInfoMap(infoMap)
    } catch {
      // API エラーはサイレントに扱う（CP は表示されないだけ）
    }
  }, [rows, gridApi])

  useEffect(() => {
    fetchCriticalPaths()
  }, [fetchCriticalPaths])

  // rows が変わったらフェッチ済みキーをリセット
  useEffect(() => {
    fetchedForRef.current = null
  }, [rows])

  // WbsItemLinkDialog 用の WbsItemLinkSummary[] を構築
  const dialogItems = useMemo<WbsItemLinkSummary[]>(
    () =>
      criticalPaths.map(link => ({
        uuid: link.uuid,
        fromWbsItemUuid: link.fromWbsItemUuid,
        toWbsItemUuid: link.toWbsItemUuid,
        fromProjectPlanUuid: link.fromProjectPlanUuid,
        toProjectPlanUuid: link.toProjectPlanUuid,
        fromName: wbsItemInfoMap.get(link.fromWbsItemUuid)?.displayName ?? '',
        toName: wbsItemInfoMap.get(link.toWbsItemUuid)?.displayName ?? '',
        fromIconUrl: wbsItemInfoMap.get(link.fromWbsItemUuid)?.iconUrl,
        toIconUrl: wbsItemInfoMap.get(link.toWbsItemUuid)?.iconUrl,
        // 端点の所属プロジェクト名/アイコン（祖先PROJECT行から解決済み）をポップアップ用に渡す
        fromProjectName: wbsItemInfoMap.get(link.fromWbsItemUuid)?.projectName,
        fromProjectIconUrl: wbsItemInfoMap.get(link.fromWbsItemUuid)
          ?.projectIconUrl,
        toProjectName: wbsItemInfoMap.get(link.toWbsItemUuid)?.projectName,
        toProjectIconUrl: wbsItemInfoMap.get(link.toWbsItemUuid)
          ?.projectIconUrl,
        fromDate: dateV2Service.fromVoToApiRequest(link.fromDate.date),
        toDate: dateV2Service.fromVoToApiRequest(link.toDate.date),
        color: link.color ?? null,
        description: link.description ?? null,
        createdAt: link.createdAt,
        createdBy: link.createdBy,
        updatedAt: link.updatedAt,
        updatedBy: link.updatedBy,
        // プロジェクト側CPはプログラム管理画面では編集不可
        editable: false,
      })),
    [criticalPaths, wbsItemInfoMap]
  )

  return { criticalPaths, wbsItemInfoMap, dialogItems }
}
